/**
 * Boards/Command-Center slice of the English catalog (W14 carve-out).
 *
 * en.ts sits against the 500-line fileSize ceiling and is the repo's
 * recorded merge hotspot — every parallel branch appends to the same tail.
 * This slice gives board vocabulary its own file (spread back into `en` in
 * en.ts, so `t()` and MessageKey are unchanged). Add new boards/COO keys
 * HERE, not in en.ts.
 */
export const enBoards = {
  'boards.files.noColumn': 'This board has no Files column, so there is nothing to collect here.',
  'boards.files.empty': 'No files attached to this board yet.',
  // ── Boards: MIRROR columns (a value read off a linked item) ──
  'boards.cell.mirrorReadOnly': 'Mirrored from a linked item — edit it there',
  'boards.cell.rollupReadOnly': 'Calculated from linked rows — edit those instead',
  // ── Boards: DEPENDENCY columns ("blocked by") ──
  'boards.cell.blockedBy': 'Blocked by — these must finish first',

  // ── Boards: a "My work" link whose row is not on screen ──
  'boards.linkedRowNotShown':
    'The row this link pointed at is not shown here — it may be filtered out, further down this board, or no longer exist.',

  // ── Boards: SOURCE column chips (kind-alignment §7) ──
  'boards.source.landing_page': 'Landing page',
  'boards.source.instagram_dm': 'Instagram DM',
  'boards.source.whatsapp': 'WhatsApp',
  'boards.source.messenger': 'Messenger',
  'boards.source.meta_ad': 'Meta ad',
  'boards.source.popup_form': 'Popup form',
  'boards.source.form': 'Form',
  'boards.source.event': 'Event',
  'boards.source.manual': 'Manual',
  'boards.source.editLabel': 'Change source',

  // ── Command Center: board action (W14) ──
  'coo.action.addToBoard': 'Add to a board',
  'coo.targetBoard': 'Target board',
  'coo.targetBoard.placeholder': '— select board —',
  'coo.targetBoard.empty': 'No boards yet. Create one in Business Hub first.',
  'coo.addToBoardNamed': 'Add to board: {name}',

  // ── BoardEngine: chrome and the states it can fail into ──
  'boards.loading': 'Loading board…',
  // `…Button`, not a bare `boards.import`: the import MODAL owns the
  // `boards.import.*` namespace below, and a key that is both a leaf and a
  // prefix reads as a typo and blocks ever nesting the catalog.
  'boards.importButton': 'Import',
  'boards.refresh': 'Refresh',
  'boards.refreshHint': 'Re-read this board now. It also refreshes on its own every couple of minutes, and whenever you come back to this tab.',
  'boards.automationsButton': 'Automations',
  'boards.dashboardHint':
    'Dashboard widgets render at the top of every view; switch to TABLE to also see rows.',
  'boards.error.load': 'Failed to load board',
  'boards.error.saveCell': 'Failed to save cell',
  'boards.error.addItem': 'Failed to add item',
  'boards.error.renameGroup': 'Failed to rename group',
  'boards.error.createGroup': 'Failed to create group',
  'boards.error.deleteItem': 'Failed to delete item',
  'boards.error.saveView': 'Failed to save view',
  'boards.error.moveCard': 'Failed to move card',

  // ── Boards: the bar that appears once rows are selected ──
  'boards.bulk.selected': '{count} selected',
  'boards.bulk.setStatus': 'Set status…',
  'boards.bulk.moveTo': 'Move to…',
  'boards.bulk.ungrouped': 'Ungrouped',
  'boards.bulk.confirmPrompt': 'Delete {count}?',
  'boards.bulk.confirmYes': 'Yes, delete',
  'boards.bulk.cancel': 'Cancel',
  'boards.bulk.delete': 'Delete',
  'boards.bulk.failed': 'That did not work',

  // ── Boards: an empty board leads with its capture link ──
  'boards.capture.emptyTitle': '{board} is empty',
  'boards.capture.emptyBody': 'This board has no capture form yet. Add a row to get started.',
  'boards.capture.addRow': 'Add a row',
  'boards.capture.shareTitle': 'Share this link and {board} fills itself',
  'boards.capture.shareBody':
    'Every submission becomes a row here — and a contact in your CRM. Put it in your bio, your emails, or on a sign.',
  'boards.capture.copied': 'Copied',
  'boards.capture.copy': 'Copy',
  'boards.capture.previewForm': 'Preview form',
  'boards.capture.editForm': 'Edit form',
  'boards.capture.hideQr': 'Hide QR code',
  'boards.capture.showQr': 'QR code',
  'boards.capture.addManually': 'Or add a row manually',

  // ── Boards: bringing a spreadsheet onto a board ──
  'boards.import.title': 'Import rows from a spreadsheet',
  'boards.import.chooseFile': 'Choose a CSV file',
  'boards.import.pasteHint': 'Or paste the rows directly:',
  'boards.import.hasHeader': 'The first row is a header',
  'boards.import.matchColumnsOne': 'Match your columns ({count} row)',
  'boards.import.matchColumnsMany': 'Match your columns ({count} rows)',
  'boards.import.unmatchedIgnored': 'Anything you leave unmatched is ignored.',
  'boards.import.columnFallback': 'Column {index}',
  'boards.import.ignore': 'Ignore',
  'boards.import.cancel': 'Cancel',
  'boards.import.preview': 'Preview',
  'boards.import.submit': 'Import {count} rows',
  'boards.import.willImport': '{imported} of {read} rows will be imported',
  'boards.import.matchedToContact': '{count} will be matched to a contact',
  'boards.import.skippedSuffix': ' · {count} skipped',
  'boards.import.problem': 'Row {row}, {column}: {message}',
  'boards.import.moreProblems': '…and {count} more',
  'boards.import.failed': 'Import failed',

  // ── Boards: the merchant's own records, projected read-only ──
  'boards.entity.liveNote': 'Live from your records — edit them at the source',
  'boards.entity.rowCountOne': '{count} row',
  'boards.entity.rowCountMany': '{count} rows',
  'boards.entity.emptyConnectedTitle': 'Nothing here yet',
  'boards.entity.emptyConnectedBodyOne':
    '{platforms} is connected, so this fills in as soon as the first record arrives.',
  'boards.entity.emptyConnectedBodyMany':
    '{platforms} are connected, so this fills in as soon as the first record arrives.',
  'boards.entity.emptyDisconnectedTitle': 'Connect a source to fill this',
  'boards.entity.emptyDisconnectedBody':
    'This board reads straight from your own records. Connect your store, calendar or ad account and it populates itself — there is nothing to set up here.',
  'boards.entity.connectSource': 'Connect a source',
  'boards.entity.previous': 'Previous',
  'boards.entity.next': 'Next',
  'boards.entity.pageOf': 'Page {page} of {pages}',
  'boards.entity.loadFailed': 'Could not load this board',

  // ── Boards: the one question a workspace with no boards is asked ──
  'boards.firstRun.question': 'What kind of business do you run?',
  'boards.firstRun.body':
    'Pick one and your workspace is set up in a second — boards, views, and a link that collects enquiries for you. You can change any of it later.',
  // Shown instead when onboarding already told us the kind — asking twice, one
  // screen apart, reads as the first answer not having landed.
  'boards.firstRun.questionKnown': 'Set up your first board',
  'boards.firstRun.bodyKnown':
    'Start with the one built for your business — boards, views, and a link that collects enquiries for you. The others are here too, and you can change any of it later.',
  'boards.firstRun.boardCountOne': '1 board',
  'boards.firstRun.boardCountMany': '{count} boards',
  'boards.firstRun.useThis': 'Use this',
  'boards.firstRun.loadFailed': 'Could not load setup options',
  'boards.firstRun.installFailed': 'Setup failed',

  // ── Boards: a column total, and the aggregate that produced it ──
  'boards.footer.tooManyToTotal': 'too many to total',
  'boards.footer.tooManyToTotalHint':
    'There are too many rows for a total you could rely on. Filter the board down and it will appear.',
  'boards.footer.notTotalled': 'not totalled',
  'boards.footer.noStoredValues':
    'This column is worked out as the board loads, so there is no stored value to total',
  'boards.footer.doesNotApplyShort': "doesn't apply",
  'boards.footer.doesNotApply': 'This total does not apply to this kind of column — pick another',
  'boards.footer.recalculating': 'recalculating',
  'boards.footer.recalculatingHint':
    'This column was changed and its figures are still being rebuilt. The total will appear once they are.',
  'boards.footer.agg.SUM': 'Sum',
  'boards.footer.agg.AVG': 'Avg',
  'boards.footer.agg.MIN': 'Min',
  'boards.footer.agg.MAX': 'Max',
  'boards.footer.agg.COUNT': 'Count',
  'boards.footer.agg.COUNT_EMPTY': 'Empty',
  'boards.footer.agg.COUNT_FILLED': 'Filled',

  // ── Boards: the phone layout, where rows are cards ──
  'boards.cards.empty': 'No items yet',
  'boards.cards.open': 'Open',
  'boards.cards.totals': 'Totals',

  // ── Boards: a parent row's children ──
  'boards.subitems.loading': 'Loading…',
  'boards.subitems.add': 'Add subitem',
  'boards.subitems.loadFailed': 'Could not load subitems',
  'boards.subitems.addFailed': 'Could not add a subitem',
  // Both numbers, deliberately: "some are hidden" leaves the merchant unable
  // to tell a handful from a hundred, and this is the only place the platform
  // ever admits that a parent holds more work than a row can show.
  //
  // Both end in the server's own instruction (subitemService.ts:200, the text
  // of the refusal a create gets past the cap). Saying only what was lost
  // leaves someone holding rows they cannot reach with nothing to do about it;
  // the platform already knows the way out, so it may as well say it.
  'boards.subitems.truncated':
    'Showing the first {shown} of {total} subitems — that is the most one row can hold. Move this work to its own board.',
  // The parent sitting exactly ON the cap: nothing is missing, but nothing more
  // fits either, which is why the button below it is greyed out.
  'boards.subitems.full':
    'This row is full at {cap} subitems. Move this work to its own board.',

  // ── Boards: mirrored and calculated values still catching up ──
  //
  // "Values", not "totals": this covers a ROLLUP's number and a MIRROR's
  // borrowed field alike. Present tense and no urgency — nothing is broken,
  // nothing needs doing, and the board's own stored cells are all current.
  //
  // THE REASSURANCE IS IN THE VISIBLE LINE, NOT THE TOOLTIP. "Updating…" makes
  // people ask whether their own work is safe, and that answer cannot live
  // somewhere half the merchants — the Capacitor build has no hover — will
  // never see. It is the same standard the subitem cap notice is held to.
  // What stays in the hint is genuinely supplementary: why it happened and
  // that it resolves itself.
  'boards.derivedStale.label': 'Linked values are updating — your own edits are saved',
  'boards.derivedStale.hint':
    'A recent change touched more rows than this board recalculates at once, so mirrored and calculated columns may lag behind for a few minutes. They update on their own; nothing needs doing.',

  // ── Business Hub: the chrome around the tabs (W14) ──
  'hub.title': 'Business Hub',
  // TRAILING SPACE IS LOAD-BEARING. A "Change" link follows this text
  // directly, and JSX strips the whitespace-only line between them, so the
  // gap has to live in the string. Trim it and you get "connections ·Change".
  'hub.detectedType': 'Set up as a {type} business from your connections · ',
  'hub.change': 'Change',
  // Task 98: the workspace read failed, so the kind is UNKNOWN — not
  // e-commerce. The layout below still falls back to e-commerce because a
  // blank hub helps nobody, and this line is what stops that fallback reading
  // as a fact.
  'hub.kindUnknown': "Couldn't load your business kind, so this is the standard layout.",
  'hub.kindUnknownRetry': 'Try again',
  // The hub's automations button and modal title read the merchant's own
  // workspace name; this is only the fallback while that read is in flight
  // or failed. It used to be our internal workspace's name for everyone.
  'hub.workspaceAutomations': 'Workspace automations',
  'hub.allBoards': 'All boards',
  'hub.active': 'Active',
  'hub.viewOverview': 'Overview',
  'hub.viewBoard': 'Board',
  'hub.boardsUnavailable': 'Custom boards unavailable: {error}',
  'hub.deleteBoardTitle': 'Delete board?',
  // TRAILING SPACE IS LOAD-BEARING — same reason as `hub.detectedType`. The
  // board's name renders bold in its own <span> between these two halves;
  // trim the lead and you get "Delete“Acme leads”?".
  'hub.deleteBoardLead': 'Delete ',
  'hub.deleteBoardTail':
    '? This permanently removes all rows, columns, automations, and history for this board.',
  'hub.cancel': 'Cancel',
  'hub.deleting': 'Deleting…',
  'hub.deleteBoard': 'Delete board',

  // ── Form builder: what a text column HOLDS ────────────────────────────
  // Naming the role is how a submitted value reaches Customer.firstName and
  // not only the board cell. Without it the server has to guess from the
  // column label against a deliberately narrow vocabulary — which cannot
  // include bare words like "Client", because on a B2B board that column
  // holds a company and the lead gets greeted as "Hi Acme".
  'boards.form.roleNone': 'Not a name',
  'boards.form.roleFullName': 'Full name',
  'boards.form.roleFirstName': 'First name',
  'boards.form.roleLastName': 'Last name',
  'boards.form.roleAria': 'What {column} holds',
  'boards.sortByColumn': 'Sort by this column',
  'boards.searchPlaceholder': 'Search this board…',
  'boards.searchClear': 'Clear search',

  // Business Hub baseline — the six cards at the top of the Overview
  // (GET /api/dashboard/baseline). Marketing to review wording.
  'hub.baseline.title': 'Your business at a glance',
  'hub.baseline.window': 'Last {days} days',
  'hub.baseline.open': 'Open',
  'hub.baseline.error': "Couldn't load this right now.",
  'hub.baseline.today.title': 'Today',
  'hub.baseline.today.count': '{count} on the calendar today',
  'hub.baseline.today.empty': 'Nothing on the calendar today.',
  'hub.baseline.today.allDay': 'All day',
  'hub.baseline.ads.title': 'Ads',
  'hub.baseline.ads.notConnected': 'No ad account connected yet.',
  'hub.baseline.ads.connect': 'Connect ads',
  'hub.baseline.ads.spend': 'Spend',
  'hub.baseline.ads.clicks': 'Clicks',
  'hub.baseline.ads.conversions': 'Conversions',
  'hub.baseline.ads.value': 'Conversion value',
  'hub.baseline.content.title': 'Content',
  'hub.baseline.content.empty': 'No posts published in this window.',
  'hub.baseline.content.posts': 'Posts',
  'hub.baseline.content.reach': 'Reach / views',
  'hub.baseline.content.likes': 'Likes',
  'hub.baseline.content.comments': 'Comments',
  'hub.baseline.content.multi': 'Several channels',
  'hub.baseline.content.row': '{posts} posts · {reach} reach · {likes} likes · {comments} comments',
  'hub.baseline.deals.title': '{noun}s',
  'hub.baseline.deals.closed': 'Closed',
  'hub.baseline.deals.open': 'Open',
  'hub.baseline.deals.counts': '{closed} closed · {open} open',
  'hub.baseline.deals.bookings': '{upcoming} upcoming bookings · {completed} completed',
  'hub.baseline.inbox.title': 'Inbox',
  'hub.baseline.inbox.open': 'Open conversations',
  'hub.baseline.inbox.unread': 'Unread',
  'hub.baseline.inbox.empty': 'No open conversations.',
  'hub.baseline.inbox.row': '{open} open · {unread} unread',
  'hub.baseline.funnels.title': 'Funnels',
  'hub.baseline.funnels.live': 'Live',
  'hub.baseline.funnels.draftTag': '(Draft)',
  'hub.baseline.funnels.pausedTag': '(Paused)',
  'hub.baseline.funnels.entered': 'Entered',
  'hub.baseline.funnels.empty': 'No funnels yet.',
  'hub.baseline.funnels.row': '{entered} entered · {completed} completed',
} as const;
